import { baseApi } from './baseApi';

export interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  // Add other patient fields as needed
}

export interface LicenseUsageRequest {
  license_id: string;
  patient_id?: string;
  access_code?: string;
  device_user_id?: string;
  organization_id?: string;
}

export interface DeviceUserRequest {
  name: string;
  email: string;
  phone: string;
}

export interface WebhookPatientDataResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    email: string;
    phone: string;
    organization_id: string;
    client_transaction_ID?: string;
    call_back_URL?: string;
    redirect_URL?: string;
    token?: string;
    createdAt: string;
    updatedAt: string;
  };
  token?: string;
}

export interface DeviceUserResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    name: string;
    email: string;
    phone: string;
    organization_id: string;
    updatedAt: string;
    createdAt: string;
  };
}

export interface UpdateSessionRequest {
  id: string
  patient_id?: string;
  organization_id?: string;
  session_status: number;
}

export interface DecodeVitalRequest {
  patientId: string;
  encodedData: string;
}

export interface PatientData {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  contact: string;
  address: string;
  street: string;
  city: string;
  state: string;
  country_code: string;
  gender_type: string;
  dob: string;
  medical_history?: string;
  allergies?: string;
  blood_group?: string;
  emergency_contact?: string;
  emergency_contact_name?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PatientDataResponse {
  success: boolean;
  data: {
    patient: PatientData;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role_id: number;
    };
  };
  message?: string;
}




export const patientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPatientById: builder.query<any, string>({
      query: (id) => ({
        url: `/patient/getPatientById/${id}`,
        method: 'GET',
      }),
      providesTags: ['Patient'],
    }),
    insertLicenseUsage: builder.mutation<any, LicenseUsageRequest>({
      query: (data) => ({
        url: '/licenseusage/insertLicenseUsage',
        method: 'POST',
        body: data,
      }),
    }),
    updateLicenseUsageSession: builder.mutation<any, UpdateSessionRequest>({
      query: (data) => ({
        url: `/licenseusage/updatesession`,
        method: 'PUT',
        body: data,
      }),
    }),
    // Create a new patient
    createPatient: builder.mutation<PatientDataResponse, Partial<PatientData>>({
      query: (patientData) => ({
        url: '/patient/create',
        method: 'POST',
        body: patientData,
      }),
      invalidatesTags: [{ type: 'Patient', id: 'LIST' }],
    }),
    decodeVitalByPatientId: builder.mutation<any, DecodeVitalRequest>({
      query: ({ patientId, encodedData }) => ({
        url: `/vital/decodeVitalByPatientId/${patientId}`,
        method: 'POST',
        body: { encodedData },
      }),
    }),
    createDeviceUser: builder.mutation<DeviceUserResponse, DeviceUserRequest>({
      query: (data) => ({
        url: '/deviceUser/create',
        method: 'POST',
        body: data,
      }),
    }),
    getWebhookPatientData: builder.query<WebhookPatientDataResponse, { deviceUserId: string; token: string }>({
      query: ({ deviceUserId, token }) => {
        const headers = new Headers();
        if (token) {
          const sanitizedToken = token.replace(/[^\x00-\x7F]/g, "").trim();
          headers.append('Authorization', `Bearer ${sanitizedToken}`);
        }
        return {
          url: `/webhook/patient-data/${deviceUserId}`,
          method: 'GET',
          headers,
        };
      },
    }),
    sendWebhook: builder.mutation<any, { call_back_URL: string; payload: any }>({
      query: (data) => ({
        url: '/webhook/send',
        method: 'POST',
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPatientByIdQuery,
  useInsertLicenseUsageMutation,
  useUpdateLicenseUsageSessionMutation,
  useCreatePatientMutation,
  useDecodeVitalByPatientIdMutation,
  useCreateDeviceUserMutation,
  useGetWebhookPatientDataQuery,
  useLazyGetWebhookPatientDataQuery,
  useSendWebhookMutation
} = patientApi;
